import React, { useEffect, useMemo, useState } from "react";
import APAudioPlayer from "./APAudioPlayer";
import { Flex, HStack, useMediaQuery } from "@chakra-ui/react";
import "react-widgets/styles.css";
import "../styles/word-select.css";
import { AddIcon } from "@chakra-ui/icons";
import APButton from "./APButton";
import { useVocabIntl } from "../hooks/useVocabTranslation";
import { IExerciserSDK } from "ap-exerciser-sdk";
import Select, { ActionMeta, createFilter, InputActionMeta, SingleValue } from "react-select";
import VirtualizedList from "./APSelect";
import { binarySplice } from "../utils";
import { VocabWord } from "../VocabModel";

interface WordSelectProps {
  addWord: (word: VocabWord) => void;
  sdk: IExerciserSDK | undefined,
  mappedWordList: Map<string, VocabWord[]>,
  selectedWords: VocabWord[],
}

function WordSelect({
                      addWord,
                      sdk,
                      selectedWords,
                      mappedWordList
                    }: WordSelectProps) {
  const { getFormattedString, APFormattedMessage } = useVocabIntl();
  const [selectedWordsLimit,] = useState(100);
  const [keyValue, setKeyValue] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [focused, setFocused] = useState<boolean>(false);
  const [selectedWord, setSelectedWord] = useState<VocabWord | null>(null);
  const [audioPath, setAudioPath] = useState("");

  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");

  useEffect(() => {
    if (selectedWord && sdk) {
      const ttsWord = selectedWord.phrase
        ? selectedWord.phrase || ""
        : selectedWord.word;

      sdk.getTTS().get(ttsWord, "fr").then((result) => {
        if (result && result.data && result.data.success && result.data.url) {
          setAudioPath(result.data.url);
        }
      })
    } else {
      setAudioPath("");
    }
  }, [selectedWord]);

  const options = useMemo(() => {
    if (keyValue && mappedWordList.has(keyValue)) {
      if (selectedWords.length > 0) {
        return binarySplice(mappedWordList.get(keyValue)!, "label", ...selectedWords)
      } else {
        return mappedWordList.get(keyValue)!;
      }
    }
    return [];
  }, [keyValue, selectedWords])

  const onInputChange = (value: string, action: InputActionMeta) => {
    if (action.action === "input-change") {
      if (value !== selectedWord?.label) {
        setSelectedWord(null);
        if (value) {
          setSearch(value)
          setKeyValue(value[0])
        } else {
          setSearch("")
        }
      }
    } else if (action.action === "input-blur") {
      setFocused(false);
    }
  }

  const addSelectedWord = () => {
    if (selectedWord) {
      addWord(selectedWord);
      setSelectedWord(null);
      setSearch("")
      setAudioPath("");
    }
  }

  const onWordChange = (wordItem: SingleValue<VocabWord>, action: ActionMeta<VocabWord>) => {
    setSelectedWord(wordItem);
    setSearch(wordItem?.label ?? "");
    setFocused(false);
  }

  const filterOptions = {
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
    matchFrom: 'start' as const
  }

  return (
    <HStack gap={["2", null, "4"]} width="100%">
      <Flex position={"relative"} flex={"1 1 100%"}>
        <Select<VocabWord>
          components={{ MenuList: VirtualizedList<VocabWord>, DropdownIndicator: null }}
          placeholder={getFormattedString('word-select-input-placeholder')}
          options={options}
          menuIsOpen={focused && search.length > 0}
          value={selectedWord}
          inputValue={focused || !selectedWord ? search : ""}
          onChange={onWordChange}
          onFocus={() => setFocused(true)}
          onInputChange={onInputChange}
          blurInputOnSelect={true}
          hideSelectedOptions={false}
          closeMenuOnSelect={true}
          filterOption={createFilter(filterOptions)}
          noOptionsMessage={() => <APFormattedMessage id={"no-options"} />}
          className={"windowed-select"}
          getOptionLabel={item => item.label}
          getOptionValue={item => item.label}
          styles={{
            menu: ({ position, fontWeight, ...provided }) => ({
              ...provided,
              position: 'absolute',
              width: "100%",
              height: "fit-content",
              maxHeight: "200px",
            }),
            control: ((base, props) => ({
              ...base,
              height: "100%",
            }))
          }}
        />
        <APAudioPlayer position="absolute" right="0.5rem" top="50%" transform={"translate(0, -50%)"} zIndex={1}
                       rounded={"full"} fontSize={"md"} file={audioPath} disableShadow />
      </Flex>
      <APButton variant={isLargeScreen ? "raised" : "raisedIcon"}
                fontSize={isLargeScreen ? "lg" : "2xl"}
                rounded={"full"}
                onClick={addSelectedWord}
                disabled={!selectedWord || selectedWords.length >= selectedWordsLimit}
                aria-label={getFormattedString('aria-label-add-button')}>
        {isLargeScreen ? <APFormattedMessage id={"add-word-button"} /> : <AddIcon />}
      </APButton>
    </HStack>
  );
}

export default WordSelect;
